import getCsrfToken from 'src/commons/apis/get-csrf-token';
import { logger } from 'src/commons/utils/logger';
import request, { RequestConfig } from './async-request/request';

const CSRF_HEADER = 'anti-csrftoken-a2z';

/*
 * Attempts per logical request. The metrics for the request are published once,
 * for the final outcome -- see the comment in async-request/request.ts.
 */
const RETRIES = 2;

/*
 * The config these helpers accept. `url` is required (each helper reads it
 * positionally, as it did when they wrapped axios.get/put/post) and `method`
 * and `retries` are owned by the helper rather than the caller.
 */
export type RetryableRequestConfig = Omit<
  RequestConfig,
  'method' | 'retries' | 'publishEndpointMetrics'
> & {
  url: string;
};

/*
 * The call sites behind these helpers went straight to axios and have never
 * published the per-endpoint Ajax series, so they do not start now -- see
 * `publishEndpointMetrics` in async-request/request.ts for why that is not a
 * free upgrade. Each keeps the counters it publishes itself.
 */
const SHARED: Pick<RequestConfig, 'retries' | 'publishEndpointMetrics'> = {
  retries: RETRIES,
  publishEndpointMetrics: false,
};

/*
 * The CSRF header for a state-changing request.
 *
 * The token is normally published to `window.__AAC_FIT_CSRF_TOKEN__` during nav
 * load (from the navState payload, or from getCsrfToken on the Trim path). It
 * can legitimately be missing anyway: the two writers race the first user
 * interaction, and the getCsrfToken call is fired deliberately OUTSIDE the nav
 * load so a slow token fetch never blocks the render -- so a user who clicks
 * fast, or whose token fetch failed, reaches this with nothing set. Re-fetching
 * once here is cheaper than the POST failing CSRF validation and being retried.
 *
 * If the re-fetch also fails the header is OMITTED and the request proceeds
 * regardless, which is what happened before: the header was set to the unset
 * global, and axios drops a header whose value is undefined. Sending the string
 * "undefined" instead would be a new, worse behaviour.
 */
const csrfHeader = async (): Promise<Record<string, string>> => {
  if (!window.__AAC_FIT_CSRF_TOKEN__) {
    try {
      window.__AAC_FIT_CSRF_TOKEN__ = await getCsrfToken();
    } catch (error) {
      const message = `asyncRequest - CSRF token missing and could not be re-fetched; sending the request without the ${CSRF_HEADER} header.`;
      // Pass the Error itself so KatalLogger keeps its name, message and stack.
      if (error instanceof Error) {
        logger.error(message, error);
      } else {
        logger.error(message, { error: String(error) });
      }
    }
  }
  const token = window.__AAC_FIT_CSRF_TOKEN__;
  return token ? { [CSRF_HEADER]: token } : {};
};

export const requestWithRetry = async <T = unknown,>(
  config: RetryableRequestConfig,
): Promise<T> =>
  request<T>({
    ...config,
    ...SHARED,
    method: 'GET',
  });

export const putRequestWithRetry = async <T = unknown,>(
  config: RetryableRequestConfig,
): Promise<T> =>
  request<T>({
    ...config,
    ...SHARED,
    method: 'PUT',
  });

export const postRequestWithRetry = async <T = unknown,>(
  config: RetryableRequestConfig,
): Promise<T> => {
  /*
   * Resolved once for the logical request, outside request()'s retry loop: a
   * retry re-sends the same token rather than re-deriving it, exactly as before.
   *
   * Caller-supplied headers are MERGED over this one, per key. They used to
   * REPLACE it wholesale -- `headers` arrived inside the spread that followed
   * the default object -- so every caller that passed a Content-Type
   * (search-ads-navigator, ajaxPostPageTitleIconInfo) silently posted with no
   * CSRF token at all. Merging means those two endpoints now receive the token
   * they were always meant to get.
   */
  const headers = { ...(await csrfHeader()), ...config.headers };
  return request<T>({
    ...config,
    ...SHARED,
    headers,
    method: 'POST',
  });
};
